import 'normalize.css';
import '../../../assets/fonts.css';

import React, { Suspense, useEffect } from 'react';
import { lazily } from 'react-lazily';
import { LearnMoreContainer } from 'src/components/v2/learn-more-container';
import { WIDGET_INITIALIZED } from 'src/constants/common';
import { COUNTRY_NAME_BY_CURRENCY_MAP } from 'src/constants/country-name-by-currency-map';
import {
  AppProvider,
  type MerchantProps,
  useAppContext,
} from 'src/context/store/app-provider';
import { useAnalytics } from 'src/hooks/use-analytics';
import { useWebviewDecision } from 'src/hooks/use-widget-decisions';
import { SegmentEventType } from 'src/types/segment';
import { dataDog } from 'src/utils/datadog/data-dog';
import { initializeDatadog } from 'src/utils/datadog/initialize-datadog';
import { getURLSearchParams } from 'src/utils/get-url-search-params';
import { renderToDecorator } from 'src/utils/render-to-decorator';

const { NonStandartLearnMore } = lazily(
  () => import('src/components/non-standart-learn-more')
);
const { LearnMoreWithSchedule } = lazily(
  () => import('src/components/learn-more-with-schedule')
);
const { LearnMoreWithInstallmentsCalculator } = lazily(
  () => import('src/components/learn-more-with-installments-calculator')
);
const { LongTermContent } = lazily(
  () => import('src/components/long-term-content')
);

export const InstallmentsWebview = () => {
  const { installmentsCount, publicKey, currency, loading, selector } =
    useAppContext();
  const decision = useWebviewDecision();
  const logAnalyticEvent = useAnalytics();

  useEffect(() => {
    if (!loading) {
      logAnalyticEvent({
        event: SegmentEventType.LEARN_MORE_POP_UP_OPENED,
        publicKey: publicKey ? 'yes' : 'no',
        merchantIntegrationType: selector ? 'snippetAndPopup' : 'popupOnly',
        planSelected: installmentsCount,
        popupType: 'standardWithoutInfo',
        merchantCountry: COUNTRY_NAME_BY_CURRENCY_MAP[currency],
        integrationType: selector ? 'native' : 'custom',
      });
    }

    initializeDatadog('webview');
  }, [
    logAnalyticEvent,
    installmentsCount,
    publicKey,
    currency,
    loading,
    selector,
  ]);

  const renderContent = () => {
    switch (decision.content) {
      case 'long-term':
        return <LongTermContent />;
      case 'non-standard':
        return <NonStandartLearnMore />;
      case 'schedule':
      case 'pay-next-month-only':
        return <LearnMoreWithSchedule />;
      default:
        return <LearnMoreWithInstallmentsCalculator />;
    }
  };

  return (
    <LearnMoreContainer
      style={{
        alignItems: 'stretch',
        maxWidth: 'none',
        borderRadius: 0,
        ...(decision.content === 'long-term'
          ? {
              height: '100%',
            }
          : null),
      }}
    >
      <Suspense fallback={null}>{renderContent()}</Suspense>
    </LearnMoreContainer>
  );
};

declare global {
  interface Window {
    InstallmentsWebview: (props: { selector: string } & MerchantProps) => void;
  }
}

window.InstallmentsWebview = ({ selector, ...props }) => {
  const searchParams = getURLSearchParams<typeof props>({
    lang: 'lang',
    currency: 'currency',
    installmentsCount: 'installmentsCount',
    price: 'price',
    partnerCharges: 'partnerCharges',
    publicKey: 'public_key',
    merchantCode: 'merchant_code',
  });

  dataDog.info('initialization', WIDGET_INITIALIZED, {
    constructorName: 'InstallmentsWebview',
    ...props,
  });

  renderToDecorator(
    selector,
    <AppProvider {...props} {...searchParams}>
      <InstallmentsWebview />
    </AppProvider>
  );
};
